<script lang="ts" setup>
import { computed, type ComponentPublicInstance, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead, type ResolvableScript } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';
import {
  getEditorialCategorySearchResults,
  getEditorialChildLocationSearchResults,
  getEditorialLocationSearchResults,
  getSearchFilters,
  type GetEditorialSearchResultsProjectResponse,
  type GetSearchFiltersGlobalCategoryResponse,
} from '@/api';
import SearchResult from '@/views/search/SearchResult.vue';
import EditorialSubscribeCard from '@/views/editorial/EditorialSubscribeCard.vue';
import EditorialHelpWidget from '@/views/editorial/EditorialHelpWidget.vue';
import EditorialProjectsMap from '@/views/editorial/EditorialProjectsMap.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { getCanonicalUrl, mentionsProjects } from '@/utilities';
import { Constants } from '@/utilities/constants';
import { useProjectJsonSchema } from '@/utilities/json-ld.ts';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faList, faMapLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  categorySlug?: string;
  parentLocationSlug?: string;
  locationSlug?: string;
  title?: string;
  subscribeCityIds?: number[];
  subscribeCategoryIds?: number[];
  enableMap?: boolean;
  filterCategoryIds?: number[];
  priceTypeId?: number | null;
}>();

const showSubscribeCard = computed(() => props.subscribeCityIds !== undefined && props.subscribeCategoryIds !== undefined);
const showHelpWidget = computed(() => Constants.Features.EditorialHelpWidget);
const isMapVisible = ref(false);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));
const loading = ref(true);
const loadingMore = ref(false);
const searchResults = ref<GetEditorialSearchResultsProjectResponse[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const hasResults = computed(() => searchResults.value.length > 0);
const hasMoreResults = computed(() => currentPage.value < totalPages.value);
const { generateProjectJsonSchema, getProjectCanonicalUrl, getProjectListingId } = useProjectJsonSchema();
const projectJsonLd = computed(() => searchResults.value.map((project) => generateProjectJsonSchema(project)));
const itemList = computed(() => ({
  '@type': 'ItemList',
  '@id': `${canonicalUrl.value}#search-results`,
  numberOfItems: searchResults.value.length,
  itemListElement: searchResults.value.map((project, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: getProjectCanonicalUrl(project),
    item: {
      '@id': getProjectListingId(project),
    },
  })),
}));

// Les options proposées sont les sous-types de la taxonomie couverts par les catégories de la page
const filterTaxonomy = ref<GetSearchFiltersGlobalCategoryResponse[]>([]);

const categoryOptions = computed(() => {
  const availableCategoryIds = new Set(props.filterCategoryIds ?? []);
  if (availableCategoryIds.size === 0) return [];

  return filterTaxonomy.value.filter((category) => category.parentId != null && (availableCategoryIds.has(category.id) || availableCategoryIds.has(category.parentId))).map((category) => ({ id: category.id, label: category.plural }));
});

const showCategoryFilter = computed(() => categoryOptions.value.length > 1);

const parseCategoryIdsQuery = (value: unknown): number[] => {
  if (typeof value !== 'string' || value.length === 0) return [];

  return value
    .split(',')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
};

const selectedCategoryIds = ref<number[]>(parseCategoryIdsQuery(route.query.categoryIds));
const hasSelectedCategories = computed(() => selectedCategoryIds.value.length > 0);

const isCategorySelected = (categoryId: number) => selectedCategoryIds.value.includes(categoryId);

const toggleCategory = (categoryId: number) => {
  selectedCategoryIds.value = isCategorySelected(categoryId) ? selectedCategoryIds.value.filter((id) => id !== categoryId) : [...selectedCategoryIds.value, categoryId];
};

const clearSelectedCategories = () => {
  selectedCategoryIds.value = [];
};

const loadFilterTaxonomy = async () => {
  try {
    const { data, error } = await getSearchFilters();
    if (error || !data) {
      console.error('Could not fetch search filters:', error);
      return;
    }
    filterTaxonomy.value = data.categories;
  } catch (error) {
    console.error('Could not fetch search filters:', error);
  }
};

// Garde « la plus récente gagne » : une réponse en retard ne doit pas écraser celle d'une requête plus récente
let fetchSequence = 0;

const fetchSearchResults = async (page = 1) => {
  const isFirstPage = page === 1;
  const sequence = ++fetchSequence;

  try {
    if (isFirstPage) {
      loading.value = true;
    } else {
      loadingMore.value = true;
    }

    const query: { page: number; categoryIds?: string } = { page };
    if (hasSelectedCategories.value) {
      query.categoryIds = selectedCategoryIds.value.join(',');
    }

    let response;
    if (props.locationSlug && props.parentLocationSlug && props.categorySlug) {
      response = await getEditorialChildLocationSearchResults({
        path: {
          editorialCategorySlug: props.categorySlug,
          parentEditorialLocationSlug: props.parentLocationSlug,
          editorialLocationSlug: props.locationSlug,
        },
        query,
      });
    } else if (props.locationSlug && props.categorySlug) {
      response = await getEditorialLocationSearchResults({
        path: {
          editorialCategorySlug: props.categorySlug,
          editorialLocationSlug: props.locationSlug,
        },
        query,
      });
    } else if (props.categorySlug) {
      response = await getEditorialCategorySearchResults({
        path: {
          editorialCategorySlug: props.categorySlug,
        },
        query,
      });
    } else {
      searchResults.value = [];
      currentPage.value = 1;
      totalPages.value = 0;
      return;
    }

    if (sequence !== fetchSequence) {
      return;
    }

    if (response.response?.ok && response.data) {
      if (isFirstPage) {
        searchResults.value = response.data.items;
      } else {
        // L'ordre du serveur peut bouger entre deux pages : on écarte les projets déjà affichés
        const knownIds = new Set(searchResults.value.map((project) => project.id));
        searchResults.value = [...searchResults.value, ...response.data.items.filter((project) => !knownIds.has(project.id))];
      }
      currentPage.value = response.data.page;
      totalPages.value = response.data.pages;
    } else if (isFirstPage) {
      searchResults.value = [];
      currentPage.value = 1;
      totalPages.value = 0;
    }
  } catch (error) {
    console.error('Error fetching editorial search results:', error);
    if (sequence === fetchSequence && isFirstPage) {
      searchResults.value = [];
      currentPage.value = 1;
      totalPages.value = 0;
    }
  } finally {
    if (sequence === fetchSequence) {
      loading.value = false;
      loadingMore.value = false;
    }
  }
};

const loadMoreResults = async () => {
  if (loadingMore.value || !hasMoreResults.value) return;
  await fetchSearchResults(currentPage.value + 1);
};

useSsgState({
  key: hasSelectedCategories.value ? null : `editorial-search-results:${props.categorySlug}/${props.parentLocationSlug}/${props.locationSlug}`,
  refs: { loading, searchResults, currentPage, totalPages },
  isLoaded: () => !loading.value,
  load: fetchSearchResults,
});

onMounted(() => {
  void loadFilterTaxonomy();
});

watch(
  () => [props.categorySlug, props.parentLocationSlug, props.locationSlug],
  () => {
    if (hasSelectedCategories.value) {
      // Le watch de la sélection relancera la recherche et nettoiera le query param
      clearSelectedCategories();
    } else {
      void fetchSearchResults();
    }
  }
);

watch(selectedCategoryIds, async () => {
  const categoryIds = hasSelectedCategories.value ? selectedCategoryIds.value.join(',') : undefined;
  await router.replace({ query: { ...route.query, categoryIds } });
  await fetchSearchResults();
});

const sectionTitle = computed(() => {
  if (props.title) {
    // Un titre contenant déjà le mot « projet(s) » / « project(s) » est affiché tel quel pour éviter le doublon avec le préfixe du gabarit « Projets de {title} »
    if (mentionsProjects(props.title)) {
      return props.title;
    }
    return t('views.editorial.search-results.header', { title: props.title });
  }
  return t('views.editorial.search-results.default');
});

const showMapToggle = computed(() => props.enableMap === true && Constants.Features.EditorialProjectsMap && hasResults.value);

// La carte n'est instanciée qu'au premier affichage, puis conservée pour éviter de recharger Google Maps à chaque bascule
const hasOpenedMap = ref(false);

const toggleMap = () => {
  isMapVisible.value = !isMapVisible.value;

  if (isMapVisible.value) {
    hasOpenedMap.value = true;
  }
};

type ProjectId = GetEditorialSearchResultsProjectResponse['id'];

const highlightedProjectId = ref<ProjectId | null>(null);
const resultElements = new Map<ProjectId, HTMLElement>();

const setResultRef = (projectId: ProjectId, target: Element | ComponentPublicInstance | null) => {
  const element = target instanceof HTMLElement ? target : target && '$el' in target && target.$el instanceof HTMLElement ? target.$el : null;

  if (element) {
    resultElements.set(projectId, element);
    return;
  }

  resultElements.delete(projectId);
};

// Un pin cliqué met en valeur sa carte dans la grille et l'amène à l'écran
const focusProject = async (projectId: ProjectId) => {
  highlightedProjectId.value = projectId;

  await nextTick();
  resultElements.get(projectId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

useHead({
  // Même stratégie que la recherche filtrée : une liste filtrée ne doit pas être indexée
  meta: () => (hasSelectedCategories.value ? [{ name: 'robots', content: 'noindex, follow' }] : []),
  script: (): ResolvableScript[] => [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: sectionTitle.value,
        url: canonicalUrl.value,
        mainEntity: itemList.value,
      }),
    },
    ...projectJsonLd.value.map((schema): ResolvableScript => ({
      type: 'application/ld+json',
      innerHTML: schema,
    })),
  ],
});
</script>

<template>
  <section>
    <SectionHeader>
      {{ sectionTitle }}
      <template v-if="$slots['header-actions'] || showMapToggle" #actions>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
          <button
            v-if="showMapToggle"
            :aria-pressed="isMapVisible"
            class="text-ph-green-600 hover:text-ph-green-800 focus:ring-ph-green-500 inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none sm:min-h-0"
            type="button"
            @click="toggleMap"
          >
            <FontAwesomeIcon :icon="isMapVisible ? faList : faMapLocationDot" class="text-[0.75em]" />
            {{ isMapVisible ? t('views.editorial.map.hide') : t('views.editorial.map.show') }}
          </button>
          <slot name="header-actions" />
        </div>
      </template>
    </SectionHeader>

    <div v-if="showCategoryFilter" :aria-label="t('views.editorial.search-results.filter-label')" class="mb-6 flex flex-wrap items-center gap-2" role="group">
      <span class="mr-1 text-sm font-medium text-gray-700">{{ t('views.editorial.search-results.filter-label') }}</span>
      <button
        v-for="option in categoryOptions"
        :key="option.id"
        :aria-pressed="isCategorySelected(option.id)"
        :class="isCategorySelected(option.id) ? 'border-ph-green-600 bg-ph-green-600 text-white' : 'hover:border-ph-green-600 hover:text-ph-green-700 border-gray-300 bg-white text-gray-700'"
        class="focus-visible:outline-ph-green-600 cursor-pointer rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
        type="button"
        @click="toggleCategory(option.id)"
      >
        {{ option.label }}
      </button>
      <button v-if="hasSelectedCategories" class="text-ph-green-600 hover:text-ph-green-800 cursor-pointer text-sm font-medium underline underline-offset-2" type="button" @click="clearSelectedCategories">
        {{ t('views.editorial.search-results.filter-clear') }}
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="text-gray-500">{{ t('views.editorial.search-results.loading') }}</div>
    </div>

    <div v-else-if="hasResults" class="space-y-6">
      <div :class="isMapVisible ? 'lg:flex lg:items-start lg:gap-6' : ''">
        <!-- La carte précède la grille dans le DOM : elle occupe la colonne de gauche sur grand écran.
             Montée une seule fois puis simplement masquée : Google réécrit le contenu du conteneur,
             et un retrait structurel laisserait Vue patcher des noeuds qu'il ne contrôle plus. -->
        <div v-if="hasOpenedMap" v-show="isMapVisible" class="lg:sticky lg:top-4 lg:w-1/2">
          <EditorialProjectsMap :price-type-id="priceTypeId" :projects="searchResults" @select="focusProject" />
        </div>

        <ul :class="isMapVisible ? 'hidden gap-6 p-1 lg:grid lg:w-1/2 lg:grid-cols-2' : 'xs:grid-cols-2 grid grid-cols-1 gap-6 p-1 md:grid-cols-3 lg:grid-cols-4'" role="list">
          <EditorialSubscribeCard v-if="showSubscribeCard" :category-ids="subscribeCategoryIds ?? []" :city-ids="subscribeCityIds ?? []" />
          <template v-for="(project, index) in searchResults" :key="project.id">
            <SearchResult :ref="(element) => setResultRef(project.id, element)" :highlighted="project.id === highlightedProjectId" :index="index" :price-type-id="priceTypeId" :project="project" />
          </template>
        </ul>
      </div>

      <div v-if="hasMoreResults" class="flex justify-center">
        <button
          :disabled="loadingMore"
          class="border-ph-green-600 text-ph-green-700 hover:bg-ph-green-50 focus-visible:outline-ph-green-600 cursor-pointer rounded-lg border-2 px-6 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-60"
          type="button"
          @click="loadMoreResults"
        >
          {{ loadingMore ? t('views.editorial.search-results.loading') : t('views.editorial.search-results.load-more') }}
        </button>
      </div>
    </div>

    <div v-else class="py-8">
      <p class="text-center text-lg font-medium text-gray-900">{{ t('views.editorial.search-results.no-results', { title: title }) }}</p>
      <EditorialHelpWidget v-if="showHelpWidget" :context="title" />
    </div>
  </section>
</template>

<style lang="css" scoped></style>
